import { apiFetch } from "../auth/apiClient";

// Thin wrappers around /api/visitor-invites/* (and a villa helper from
// /api/users/me). Auth comes from the bearer token apiFetch reads out of
// localStorage — same pattern as security/api.js and board/api.js. Mirrors
// the mobile client (`mobile/src/api/visitors.ts`) so the resident's "visitor
// pass" works identically on web.

export const visitorsApi = {
  // The caller's APPROVED villa memberships, as { id, number } — used to pick
  // which villa a pass is for. The server still authorizes invite creation by
  // real membership; this only drives the picker.
  myVillas: async ({ signal } = {}) => {
    const me = await apiFetch("/api/users/me", {
      method: "GET",
      authRequired: true,
      signal,
    });
    return (me.memberships || [])
      .filter((m) => m.approval_status === "APPROVED" && m.villa)
      .map((m) => ({ id: m.villa.id, number: m.villa.villa_number }));
  },

  // Resident creates a pre-approval pass → Invite DTO with `code` (the signed
  // QR payload) + `short_code` (6-char manual fallback).
  createInvite: (body, { signal } = {}) =>
    apiFetch("/api/visitor-invites", {
      method: "POST",
      authRequired: true,
      body,
      signal,
    }),

  // The caller's own passes, newest-first (max 200).
  listInvites: ({ signal } = {}) =>
    apiFetch("/api/visitor-invites", {
      method: "GET",
      authRequired: true,
      signal,
    }),

  // Revoke a pass the caller created → Invite DTO with status REVOKED.
  revokeInvite: (inviteId, { signal } = {}) =>
    apiFetch(`/api/visitor-invites/${inviteId}`, {
      method: "DELETE",
      authRequired: true,
      signal,
    }),

  // ── Supervisor / ops ────────────────────────────────────────────────────

  // Role-scoped paginated visitor log (supervisors see community-wide).
  listVisitors: ({ status, date, limit = 25, cursor, signal } = {}) => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (date) p.set("date", date);
    p.set("limit", String(limit));
    if (cursor) p.set("cursor", cursor);
    return apiFetch(`/api/visitors?${p.toString()}`, {
      method: "GET",
      authRequired: true,
      signal,
    });
  },

  // Gate supervisor overrides a PENDING visitor → APPROVED (with mandatory note).
  overrideVisitor: (visitorId, { override_note }, { signal } = {}) =>
    apiFetch(`/api/visitors/${visitorId}/override`, {
      method: "POST",
      authRequired: true,
      body: { override_note },
      signal,
    }),

  // Gate operator checks an APPROVED visitor in → INSIDE.
  checkIn: (visitorId, { signal } = {}) =>
    apiFetch(`/api/visitors/${visitorId}/check-in`, {
      method: "POST",
      authRequired: true,
      signal,
    }),

  // Gate operator checks an INSIDE visitor out → LEFT.
  checkOut: (visitorId, { signal } = {}) =>
    apiFetch(`/api/visitors/${visitorId}/check-out`, {
      method: "POST",
      authRequired: true,
      signal,
    }),
};
